import type { PageResult } from '@jiaofa/api-types'
import { request } from './request'

export type DroneStatus = 'IDLE' | 'TASK' | 'CHARGING' | 'MAINTENANCE' | 'FAULT' | 'OFFLINE'
export type DroneTaskStatus = 'WAITING' | 'DRONE_LOADING' | 'DRONE_TAKEOFF' | 'DRONE_FLYING' | 'ARRIVED' | 'COMPLETED' | 'EXCEPTION'
export type DroneFaultStatus = 'OPEN' | 'PROCESSING' | 'RESOLVED'
export type DroneMaintenanceStatus = 'SCHEDULED' | 'DOING' | 'DONE'
export type DroneMaintenanceType = 'REPAIR' | 'MAINTAIN' | 'INSPECT'

export interface Drone {
  id: string
  droneNo: string
  name?: string
  model?: string
  deviceType?: string
  warehouseCode?: string
  storeId?: string
  storeName?: string
  status: DroneStatus
  battery: number
  currentLng?: string
  currentLat?: string
  currentLocation?: string
  online: boolean
  missionCount: number
  lastCommAt?: string
  remark?: string
  createdAt?: string
}

export interface DroneOverview {
  total: number
  onlineCount: number
  taskCount: number
  offlineCount: number
  faultCount: number
  todayMissions: number
}

export interface DroneSavePayload {
  id?: string
  droneNo: string
  name?: string
  model?: string
  deviceType?: string
  warehouseCode?: string
  storeId?: string
  battery?: number
  status?: DroneStatus
  remark?: string
}

export interface DroneListParams {
  page?: number
  pageSize?: number
  keyword?: string
  status?: DroneStatus
  online?: 0 | 1
}

export interface DroneTask {
  deliveryId: string
  deliveryNo: string
  orderNo: string
  storeName?: string
  droneCode?: string
  droneName?: string
  toAddress?: string
  distanceKm?: string
  fee: number
  status: DroneTaskStatus
  pickupCode?: string
  exceptionReason?: string
  estimateArriveAt?: string
  deliveredAt?: string
  createdAt?: string
}

export interface DroneFlight extends DroneTask {
  flightDuration?: string
  waypointCount: number
}

export interface DroneWaypoint {
  latitude: string
  longitude: string
  timestamp: number
  status?: string
}

export interface DroneTrackNode {
  status: string
  desc: string
  time: string
}

export interface DroneFlightDetail extends DroneFlight {
  waypoints: DroneWaypoint[]
  timeline: DroneTrackNode[]
}

export interface DroneTaskListParams {
  page?: number
  pageSize?: number
  status?: DroneTaskStatus
  keyword?: string
}

export interface DroneFlightListParams extends DroneTaskListParams {
  droneNo?: string
}

export interface DroneFault {
  id: string
  faultNo: string
  droneId: string
  droneNo: string
  droneName?: string
  type: string
  level?: string
  description?: string
  source: string
  status: DroneFaultStatus
  resolveNote?: string
  createdAt: string
}

export interface DroneFaultListParams {
  page?: number
  pageSize?: number
  droneId?: string
  status?: DroneFaultStatus
  level?: string
}

export interface DroneFaultSavePayload {
  droneId: string
  type: string
  level?: string
  description?: string
}

export interface DroneFaultStatusPayload {
  status: DroneFaultStatus
  resolveNote?: string
}

export interface DroneMaintenance {
  id: string
  maintenanceNo: string
  droneId: string
  droneNo: string
  droneName?: string
  type: DroneMaintenanceType
  status: DroneMaintenanceStatus
  content?: string
  cost: number
  scheduledAt?: string
  doneAt?: string
  operator?: string
  result?: string
  createdAt: string
}

export interface DroneMaintenanceListParams {
  page?: number
  pageSize?: number
  droneId?: string
  status?: DroneMaintenanceStatus
  type?: DroneMaintenanceType
}

export interface DroneMaintenanceSavePayload {
  droneId: string
  type: DroneMaintenanceType
  content?: string
  cost?: number
  scheduledAt?: string
  operator?: string
}

export interface DroneMaintenanceStatusPayload {
  status: DroneMaintenanceStatus
  result?: string
  doneAt?: string
}

export const droneApiReady = true as const

export const fetchDrones = async (params: DroneListParams): Promise<PageResult<Drone>> =>
  request<PageResult<Drone>>({
    url: '/drones',
    method: 'GET',
    params,
  })

export const fetchDroneOverview = async (): Promise<DroneOverview> =>
  request<DroneOverview>({
    url: '/drones/overview',
    method: 'GET',
  })

export const fetchDroneDetail = async (id: string): Promise<Drone> =>
  request<Drone>({
    url: `/drones/${id}`,
    method: 'GET',
  })

export const createDrone = async (payload: DroneSavePayload): Promise<void> =>
  request<void, DroneSavePayload>({
    url: '/drones',
    method: 'POST',
    data: payload,
  })

export const updateDrone = async (payload: DroneSavePayload): Promise<void> => {
  if (!payload.id) throw new Error('缺少无人机 id')
  return request<void, DroneSavePayload>({
    url: '/drones',
    method: 'PUT',
    data: payload,
  })
}

export const deleteDrone = async (id: string): Promise<void> =>
  request<void>({
    url: `/drones/${id}`,
    method: 'DELETE',
  })

export const syncDrones = async (): Promise<DroneOverview> =>
  request<DroneOverview>({
    url: '/drones/sync',
    method: 'POST',
  })

export const fetchDroneTasks = async (params: DroneTaskListParams): Promise<PageResult<DroneTask>> =>
  request<PageResult<DroneTask>>({
    url: '/drone-tasks',
    method: 'GET',
    params,
  })

export const fetchDroneTaskDetail = async (deliveryId: string): Promise<DroneFlightDetail> =>
  request<DroneFlightDetail>({
    url: `/drone-tasks/${deliveryId}`,
    method: 'GET',
  })

export const assignDroneTask = async (deliveryId: string, droneId: string): Promise<DroneTask> =>
  request<DroneTask, { droneId: string }>({
    url: `/drone-tasks/${deliveryId}/assign`,
    method: 'PATCH',
    data: { droneId },
  })

export const updateDroneTaskStatus = async (deliveryId: string, status: DroneTaskStatus): Promise<DroneTask> =>
  request<DroneTask, { status: DroneTaskStatus }>({
    url: `/drone-tasks/${deliveryId}/status`,
    method: 'PATCH',
    data: { status },
  })

export const openDroneTaskBox = async (deliveryId: string, type?: 'pick' | 'send'): Promise<{ code: number; msg: string }> =>
  request<{ code: number; msg: string }, { type?: 'pick' | 'send' }>({
    url: `/drone-tasks/${deliveryId}/open-box`,
    method: 'POST',
    data: { type },
  })

export const closeDroneTaskBox = async (deliveryId: string, type?: 'pick' | 'send'): Promise<{ code: number; msg: string }> =>
  request<{ code: number; msg: string }, { type?: 'pick' | 'send' }>({
    url: `/drone-tasks/${deliveryId}/close-box`,
    method: 'POST',
    data: { type },
  })

export const fetchDroneFlights = async (params: DroneFlightListParams): Promise<PageResult<DroneFlight>> =>
  request<PageResult<DroneFlight>>({
    url: '/drone-tasks/flights',
    method: 'GET',
    params,
  })

export const fetchDroneFlightDetail = async (deliveryId: string): Promise<DroneFlightDetail> =>
  request<DroneFlightDetail>({
    url: `/drone-tasks/flights/${deliveryId}`,
    method: 'GET',
  })

export const fetchDroneFaults = async (params: DroneFaultListParams): Promise<PageResult<DroneFault>> =>
  request<PageResult<DroneFault>>({
    url: '/drones/faults',
    method: 'GET',
    params,
  })

export const createDroneFault = async (payload: DroneFaultSavePayload): Promise<void> =>
  request<void, DroneFaultSavePayload>({
    url: '/drones/faults',
    method: 'POST',
    data: payload,
  })

export const updateDroneFaultStatus = async (id: string, payload: DroneFaultStatusPayload): Promise<void> =>
  request<void, DroneFaultStatusPayload>({
    url: `/drones/faults/${id}/status`,
    method: 'PATCH',
    data: payload,
  })

export const deleteDroneFault = async (id: string): Promise<void> =>
  request<void>({
    url: `/drones/faults/${id}`,
    method: 'DELETE',
  })

export const fetchDroneMaintenances = async (params: DroneMaintenanceListParams): Promise<PageResult<DroneMaintenance>> =>
  request<PageResult<DroneMaintenance>>({
    url: '/drones/maintenances',
    method: 'GET',
    params,
  })

export const createDroneMaintenance = async (payload: DroneMaintenanceSavePayload): Promise<void> =>
  request<void, DroneMaintenanceSavePayload>({
    url: '/drones/maintenances',
    method: 'POST',
    data: payload,
  })

export const updateDroneMaintenanceStatus = async (id: string, payload: DroneMaintenanceStatusPayload): Promise<void> =>
  request<void, DroneMaintenanceStatusPayload>({
    url: `/drones/maintenances/${id}/status`,
    method: 'PATCH',
    data: payload,
  })

export const deleteDroneMaintenance = async (id: string): Promise<void> =>
  request<void>({
    url: `/drones/maintenances/${id}`,
    method: 'DELETE',
  })
